<template>
  <div class="instance-form">
    <form @submit.prevent="handleSubmit">
      <div class="p-fluid p-formgrid p-grid">
        <div class="p-field p-col-12">
          <label for="instanceName">Nome da Instância*</label>
          <InputText 
            id="instanceName" 
            v-model="form.instance_name" 
            :class="{ 'p-invalid': submitted && !form.instance_name }"
            aria-describedby="instance-name-error"
          />
          <small id="instance-name-error" class="p-error" v-if="submitted && !form.instance_name">
            Nome da instância é obrigatório.
          </small>
        </div>

        <div class="p-field p-col-12">
          <label for="webhookUrl">URL do Webhook (opcional)</label>
          <InputText id="webhookUrl" v-model="form.webhook_url" />
          <small class="p-text-secondary">
            URL para receber notificações de mensagens e eventos.
          </small>
        </div>

        <div class="p-field p-col-12">
          <label for="event_types">Eventos para Notificação (opcional)</label>
          <MultiSelect
            id="event_types"
            v-model="form.events"
            :options="availableEvents"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione os eventos"
            display="chip"
          />
          <small class="p-text-secondary">
            Eventos que serão notificados no webhook.
          </small>
        </div>

        <div class="p-field p-col-12">
          <label for="webhook_enabled">Habilitar Webhook</label>
          <div class="p-field-checkbox">
            <Checkbox id="webhook_enabled" v-model="form.webhook_enabled" :binary="true" />
            <label for="webhook_enabled" class="p-checkbox-label">
              {{ form.webhook_enabled ? 'Habilitado' : 'Desabilitado' }}
            </label>
          </div>
        </div>

        <div class="p-field p-col-12">
          <h4>Configurações Adicionais</h4>
        </div>

        <div class="p-field p-col-12">
          <label for="message_prefix">Prefixo de Mensagens (opcional)</label>
          <InputText id="message_prefix" v-model="form.settings.message_prefix" />
          <small class="p-text-secondary">
            Prefixo a ser adicionado em todas as mensagens enviadas.
          </small>
        </div>

        <div class="p-field p-col-12">
          <label for="auto_typing">Simulação de Digitação</label>
          <div class="p-field-checkbox">
            <Checkbox id="auto_typing" v-model="form.settings.auto_typing" :binary="true" />
            <label for="auto_typing" class="p-checkbox-label">
              {{ form.settings.auto_typing ? 'Habilitado' : 'Desabilitado' }}
            </label>
          </div>
          <small class="p-text-secondary">
            Simula digitação antes de enviar mensagens.
          </small>
        </div>

        <div class="p-field p-col-12">
          <label for="typing_time">Tempo de Digitação (segundos)</label>
          <InputNumber id="typing_time" v-model="form.settings.typing_time" :min="1" :max="10" />
          <small class="p-text-secondary">
            Tempo em segundos para simulação de digitação.
          </small>
        </div>
      </div>

      <div class="form-actions">
        <Button 
          type="button" 
          label="Cancelar" 
          class="p-button-outlined p-button-secondary" 
          @click="$emit('cancel')" 
        />
        <Button 
          type="submit" 
          label="Salvar" 
          class="p-button-primary" 
          :loading="loading" 
        />
      </div>
    </form>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted } from 'vue';

export default {
  name: 'InstanceForm',
  props: {
    initialInstance: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['submit', 'cancel'],
  setup(props, { emit }) {
    const submitted = ref(false);
    
    const form = reactive({
      instance_name: '',
      webhook_url: '',
      webhook_enabled: false,
      events: [],
      settings: {
        message_prefix: '',
        auto_typing: true,
        typing_time: 3
      }
    });
    
    const availableEvents = [
      { label: 'Mensagens', value: 'messages' },
      { label: 'Status do QR Code', value: 'qrcode' },
      { label: 'Status da Conexão', value: 'connection' },
      { label: 'Atualização de Status', value: 'status' },
      { label: 'Mensagens Enviadas', value: 'messages:send' },
      { label: 'Confirmação de Leitura', value: 'messages:ack' }
    ];
    
    onMounted(() => {
      // Preenche o formulário com os dados da instância se existirem
      if (props.initialInstance && props.initialInstance.id) {
        form.instance_name = props.initialInstance.instance_name || '';
        form.webhook_url = props.initialInstance.webhook_url || '';
        form.webhook_enabled = props.initialInstance.webhook_enabled || false;
        form.events = props.initialInstance.events || [];
        
        // Mescla as configurações existentes com os valores padrão
        if (props.initialInstance.settings) {
          form.settings = {
            ...form.settings,
            ...props.initialInstance.settings
          };
        }
      }
    });
    
    const handleSubmit = () => {
      submitted.value = true;
      
      if (!form.instance_name) {
        return;
      }
      
      const formData = {
        instance_name: form.instance_name,
        webhook_url: form.webhook_url,
        webhook_enabled: form.webhook_enabled,
        events: form.events,
        settings: form.settings
      };
      
      // Se estiver editando, mantém o ID
      if (props.initialInstance && props.initialInstance.id) {
        formData.id = props.initialInstance.id;
      }
      
      emit('submit', formData);
    };
    
    return {
      form,
      submitted,
      availableEvents,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.instance-form {
  background-color: var(--paper);
  border-radius: 0;
  padding: 1.5rem;
  border: 1px solid var(--rule);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 2rem;
}

h4 {
  color: var(--ink);
  margin-bottom: 0.5rem;
  margin-top: 1.5rem;
  font-size: 1.1rem;
  border-bottom: 1px solid var(--rule);
  padding-bottom: 0.5rem;
}

.p-field {
  margin-bottom: 1.5rem;
}

.p-field-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.p-checkbox-label {
  margin-left: 0.5rem;
}

.p-text-secondary {
  color: var(--ink-soft);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style> 